import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../../shared/utils/dropdownPositioning';
import { SortIcon, CheckIcon, ChevronIcon } from './icons';
import { SORT_OPTIONS, getSortOption, generateCustomFieldSortOptions } from './SortOptions';
import { sortTasks } from './sortUtils';

// Re-export for backward compatibility
export { SORT_OPTIONS, getSortOption, generateCustomFieldSortOptions } from './SortOptions';
export { sortTasks } from './sortUtils';

/**
 * SortDropdown Component
 * 
 * Dropdown for selecting how to sort Kanban cards within columns.
 * Supports both built-in sort options and custom field sorting.
 */
const SortDropdown = ({
  currentSort = 'manual',
  onSortChange,
  customFields = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Generate custom field sort options
  const customFieldOptions = useMemo(() => 
    generateCustomFieldSortOptions(customFields),
    [customFields]
  );

  const currentOption = getSortOption(currentSort, customFields);

  // Calculate dropdown position with edge detection
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 260));
    }
  };

  // Open dropdown
  const handleToggle = () => {
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current); // Close any other open dropdowns first
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Listen for global close events (when another dropdown opens or view zooms/pans)
  useEffect(() => {
    const handleCloseAll = () => setIsOpen(false);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionId) => {
    onSortChange?.(optionId);
    setIsOpen(false);
  };

  const isActive = currentSort !== 'manual';

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isActive ? 'toolbar-btn--active' : ''}`}
        onClick={handleToggle}
        title="Sort cards within columns"
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon">
            <SortIcon />
          </span>
          <span className="toolbar-btn__label">
            {isActive ? `Sort: ${currentOption.label}` : 'Sort'}
          </span>
        </span>
        <span className={`toolbar-btn__chevron ${isOpen ? 'toolbar-btn__chevron--open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="sort-dropdown__menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="sort-dropdown__header">
            Sort By
          </div>
          {SORT_OPTIONS.map(option => (
            <button
              key={option.id}
              className={`sort-dropdown__item ${currentSort === option.id ? 'sort-dropdown__item--selected' : ''}`}
              onClick={() => handleSelect(option.id)}
            >
              <span className="sort-dropdown__item-check">
                {currentSort === option.id && <CheckIcon />}
              </span>
              <span className="sort-dropdown__item-content">
                <span className="sort-dropdown__item-label">{option.label}</span>
                {option.description && (
                  <span className="sort-dropdown__item-description">{option.description}</span>
                )}
              </span>
            </button>
          ))}
          
          {/* Custom Fields Section */}
          {customFieldOptions.length > 0 && (
            <>
              <div className="sort-dropdown__divider" />
              <div className="sort-dropdown__header">
                Custom Fields
              </div>
              {customFieldOptions.map(option => (
                <button
                  key={option.id}
                  className={`sort-dropdown__item ${currentSort === option.id ? 'sort-dropdown__item--selected' : ''}`}
                  onClick={() => handleSelect(option.id)}
                >
                  <span className="sort-dropdown__item-check">
                    {currentSort === option.id && <CheckIcon />}
                  </span>
                  <span className="sort-dropdown__item-content">
                    <span className="sort-dropdown__item-label">{option.label}</span>
                    {option.description && (
                      <span className="sort-dropdown__item-description">{option.description}</span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default SortDropdown;
